import { SPACE_BLOCK, SPACE_SECTION } from "../layoutTokens";

/**
 * The two-column form grid both User Information sections share.
 *
 * Shared rather than repeated so the two sections cannot drift apart — a grid that is 2-up in one
 * section and 2-up-with-a-different-gap in the next reads as a rendering bug.
 *
 * Asymmetric gaps are deliberate and come from the prototype: 24px between columns, 16px between
 * rows. Equal gaps make the two columns read as one four-field block, because the horizontal
 * distance between a field and its neighbour matches the vertical distance to the field below it.
 *
 * ## The collapse point is `lg`, not `md` (TA4236901)
 *
 * It was `md` (679px), matching the prototype's own `@media(max-width:820px)`. Raised to **`lg`
 * (959px)** so the form is single-column across the whole 680–959px band as well — that band is the
 * two-pane tablet layout, where the detail card is ~440px wide and two columns of fields inside it
 * put a label and its input on separate lines. TA4236901 asks for the same thing
 * (`768–1023px: .form-row { grid-template-columns: 1fr }`).
 *
 * **This is the shared panel, so it changes My Info as well as User Manager** — intended, and the
 * reason the decision to make the shared panel responsive was taken explicitly rather than scoping
 * the work to User Manager alone.
 */
export const FIELD_GRID = {
  columnGap: SPACE_SECTION,
  display: "grid",
  gridTemplateColumns: { lg: "repeat(2, minmax(0, 1fr))", xs: "1fr" },
  marginTop: SPACE_BLOCK,
  rowGap: SPACE_BLOCK,

  /*
   * One required indicator, not two. Interstate's `required` already puts the **word** "(required)"
   * in the label, and MUI then appends its own asterisk on top — so the label read "First Name
   * (required) *", stating the same fact twice.
   *
   * The word is the one to keep: the asterisk is already `aria-hidden`, so it was never doing
   * anything for assistive technology, and a glyph alone would lean on a visual convention the word
   * states outright (FR-050). Hidden at the grid level rather than per field, so a field added later
   * cannot reintroduce it.
   */
  "& .MuiFormLabel-asterisk": { display: "none" }
};

/**
 * Spans a field across the whole grid, for the ones the prototype gives their own row: Title,
 * Company, and both email addresses.
 *
 * `1 / -1` — first grid line to last — rather than a breakpoint-conditional span, because below the
 * collapse point the grid is already a single column, where first-to-last is exactly that one
 * column. So the same value is correct at both breakpoints and the rule needs no variant of its own
 * — which is also why raising the collapse point from `md` to `lg` did not touch this.
 */
export const FULL_WIDTH_FIELD = { gridColumn: "1 / -1" };
